<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ProgressSteps from "@/components/website/ProgressSteps.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";

const store = useStore();

const emit = defineEmits(['next', 'back']);
const industry = ref('');
const country = ref('');
const region = ref('');
const location = ref('');

const regions = ref({});
const locations = ref({});
const loading = computed(() => store.getters['appsumoWebsite/loading']);
const errors = computed(() => store.getters['appsumoWebsite/errors']);

const props = defineProps({
  countries: {
    required: true,
    type: Object,
  },
  industries: {
    required: true,
    type: Object,
  },
  freeWebsite: {
    required: true,
    type: Boolean,
  },
});

onMounted(async () => {
  const country = store.getters['appsumoWebsite/country'];
  if (country !== '') {
    region.value = store.getters['appsumoWebsite/region'];
    regions.value = await store.dispatch('appsumoWebsite/fetchLocations', country);
  }

  const regionGetter = store.getters['appsumoWebsite/region'];
  if (regionGetter !== '') {
    location.value = store.getters['appsumoWebsite/location'];
    locations.value = await store.dispatch('appsumoWebsite/fetchLocations', regionGetter);
  }
});

watch(country, async (newValue) => {
  if (country.value === store.getters['appsumoWebsite/country']) {
    return;
  }

  if (newValue === '') {
    region.value = '';
    regions.value = {};
    return;
  }

  region.value = '';
  regions.value = await store.dispatch('appsumoWebsite/fetchLocations', newValue);

  clearErrors('country');
});

watch(region, async (newValue) => {
  if (country.value === store.getters['appsumoWebsite/region']) {
    return;
  }

  if (newValue === '') {
    location.value = '';
    locations.value = {};
    return;
  }

  location.value = '';
  locations.value = await store.dispatch('appsumoWebsite/fetchLocations', newValue);
});

watch(industry, (newValue) => {
  if (newValue !== '') {
    clearErrors('industry');
  }
});

const clearErrors = field => store.dispatch('appsumoWebsite/clearError', field);

const handleContinue = async () => {
  await store.dispatch('appsumoWebsite/setLoading', true);

  const errors = {};

  if (!industry.value) {
    errors.industry = {message: 'Please fill the field industry'};
  }

  if (!country.value) {
    errors.country = {message: 'Please fill the field country'};
  }

  if (Object.keys(errors).length > 0) {
    await store.dispatch('appsumoWebsite/setErrors', {errors});
    await store.dispatch('appsumoWebsite/setLoading', false);

    return;
  }

  saveToState();

  if (props.freeWebsite) {
    await store.dispatch('appsumoWebsite/create');

    return;
  }

  emit('next');
};


const handleBack = () => {
  saveToState();

  store.dispatch('appsumoWebsite/setErrors', {});

  emit('back');
}

const saveToState = () => {
  store.dispatch('appsumoWebsite/setStepTwo', {
    industry: industry.value,
    country: country.value,
    region: region.value,
    location: location.value,
  });
}

industry.value = store.getters['appsumoWebsite/industry'];
country.value = store.getters['appsumoWebsite/country'];
region.value = store.getters['appsumoWebsite/region'];
location.value = store.getters['appsumoWebsite/location'];
</script>

<template>
  <div
    class="tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-text-center tw-max-w-default-container tw-mx-auto tw-py-6 tw-min-h-[680px]"
  >
    <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-gap-8 2xl:tw-w-2/5 tw-w-3/4">
      <div class="tw-flex tw-flex-col">
        <h1 class="tw-text-4xl tw-font-bold">
          Website Information
        </h1>
      </div>

      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <label
          for="industry"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Your website's industry:
        </label>
        <CustomMultiselect
          id="industry"
          v-model="industry"
          class="tw-text-left"
          :close-on-select="true"
          :options="industries"
          :searchable="true"
          :validation-errors="errors.industry"
        />
      </div>

      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <label
          for="country"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Location of business:
        </label>
        <CustomMultiselect
          id="country"
          v-model="country"
          class="tw-text-left"
          :close-on-select="true"
          :options="countries"
          :searchable="true"
          :validation-errors="errors.country"
          :can-deselect="false"
        />
      </div>

      <div
        v-if="Object.keys(regions).length > 0"
        class="tw-flex tw-flex-col tw-w-full tw-gap-2"
      >
        <label
          for="region"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Region of business <span class="tw-italic">(Optional)</span>:
        </label>
        <CustomMultiselect
          id="region"
          v-model="region"
          class="tw-text-left"
          :close-on-select="true"
          :options="regions"
          :searchable="true"
          groups
        />
      </div>

      <div
        v-if="Object.keys(locations).length > 0"
        class="tw-flex tw-flex-col tw-w-full tw-gap-2"
      >
        <label
          for="location"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Location of business <span class="tw-italic">(Optional)</span>:
        </label>
        <CustomMultiselect
          id="location"
          v-model="location"
          class="tw-text-left"
          :close-on-select="true"
          :options="locations"
          :searchable="true"
          groups
        />
      </div>

      <div class="tw-flex tw-w-full tw-justify-between">
        <DefaultButton
          variant="base"
          :disabled="loading"
          @click="handleBack"
        >
          Back
        </DefaultButton>

        <DefaultButton
          :disabled="loading"
          @click="handleContinue"
        >
          Continue
        </DefaultButton>
      </div>

      <div class="tw-flex tw-flex-col">
        <ProgressSteps
          :current-step="2"
          :hide-step-three="freeWebsite"
        />
      </div>
    </div>
  </div>
</template>
